// PotreeViewer3 리팩토링 - 영역 측정 전용 훅
// 역할:
// - Potree MeasuringTool 생성/관리
// - 영역 측정 시작/종료
// - 마지막 측정 결과를 GeoJSON/Store로 내보내기
// - measurementsRef 를 외부에서 사용할 수 있도록 노출

import { useEffect, useRef, useState } from "react";
import { useLastAreaMeasurementStore } from "../Store/PotreeStore";
import { exportMeasurementToGeoJSON } from "../util/pathGeometry";

/**
 * useAreaMeasurement
 *
 * @param {any} potreeViewer - Potree Viewer 인스턴스
 * @returns {
 *   measurementsRef,        // 마지막 측정 포인트 목록 (EPSG5186 좌표)
 *   isMeasuring, setIsMeasuring,
 *   startAreaMeasurement,   // 측정 시작
 *   endAreaMeasurement,     // 측정 종료 + Store 기록
 *   exportMeasurements      // 최신 측정을 GeoJSON으로 변환 (현재는 side-effect만)
 * }
 */
export function useAreaMeasurement(potreeViewer) {
  const [isMeasuring, setIsMeasuring] = useState(false);
  const measurementsRef = useRef([]);
  const measuringToolRef = useRef(null);

  // MeasuringTool 생성/관리
  useEffect(() => {
    if (!potreeViewer || !window.Potree) return;

    measuringToolRef.current = new window.Potree.MeasuringTool(potreeViewer);

    return () => {
      // Potree 쪽에서 별도 dispose 로직을 가지고 있으므로
      // 여기서는 참조만 정리한다.
      measuringToolRef.current = null;
    };
  }, [potreeViewer]);

  // 측정 작업 토글 (시작)
  const startAreaMeasurement = () => {
    if (!measuringToolRef.current) return;
    measurementsRef.current = [];

    measuringToolRef.current.startInsertion({
      showArea: true,
      showDistances: true,
      showEdges: true,
      closed: true,
      maxMarkers: 20,
      name: "Area Measurement",
    });
    // 원본 코드에서도 isMeasuring 토글은 주석 처리 상태였으므로 그대로 둔다.
    // setIsMeasuring(true);
  };

  // 측정 종료 + Store 저장
  const endAreaMeasurement = () => {
    if (!measuringToolRef.current || !potreeViewer) return;
    const measurements = potreeViewer.scene.measurements;

    measurementsRef.current = [];

    // 모달/후속 로직에서 쓰기 위해 "마지막 측정"을 store에 저장해둠
    // (scene.measurements가 나중에 정리되더라도 유지)
    const latest = measurements?.at?.(-1);
    if (latest) {
      const points = latest.points.map((p) => ({
        x: p.position.x,
        y: p.position.y,
        z: p.position.z,
      }));
      measurementsRef.current.push(...points);

      const areaLabelText = latest.areaLabel?.text || "";
      const area_m2 = parseFloat(areaLabelText.replace(/[^\d.]/g, "")) || 0;
      useLastAreaMeasurementStore.getState().setLastAreaMeasurement({
        name: latest.name || "-",
        points,
        area_m2,
        areaLabelText: areaLabelText || "-",
      });
    } else {
      // 측정이 없으면 store도 비움
      useLastAreaMeasurementStore.getState().clearLastAreaMeasurement();
    }

    // 원본 코드에서도 scene.measurements 정리 및 isMeasuring 토글은 주석 처리 상태.
    // measurements.forEach((measurement) => {
    //   potreeViewer.scene.removeMeasurement(measurement);
    // });
    // setIsMeasuring(false);
  };

  // 측정 내보내기
  const exportMeasurements = () => {
    if (!potreeViewer) return null;
    const measurements = potreeViewer?.scene?.measurements;
    if (measurements?.length > 0) {
      const latest = measurements.at(-1);
      const geojson = exportMeasurementToGeoJSON(latest);
      // 원본 코드도 geojson을 생성만 하고 사용하지 않으므로,
      // 여기서도 side-effect 없이 그대로 유지.
      return geojson;
    }
    return null;
  };

  return {
    measurementsRef,
    isMeasuring,
    setIsMeasuring,
    startAreaMeasurement,
    endAreaMeasurement,
    exportMeasurements,
  };
}

